import AuthDialogProvider from '@common/components/auth-dialog/AuthDialogProvider';
import PrivateRoute from '@common/components/PrivateRoute';
import Sidebar from '@common/components/Sidebar';
import RecommendedExpressProvider from '@features/recommended-express/RecommendedExpressProvider';
import SplashScreen from '@common/components/SplashScreen';
import { miniGamesClaimWin } from '@common/helpers/miniGames';
import { useAppDispatch, useAppSelector } from '@common/hooks/store';
import { isDomainZone } from '@common/utils/lang';
import config from '@config';
import AdmiralGameIframe from '@features/admiral/components/games/AdmiralGameIframe';
import CheckerIosVersion from '@features/app/CheckerIosVersion';
import { selectAppPlatform } from '@features/app/slice/appSlice';
import { getUserId, userLoggedIn } from '@features/auth/slice/authSlice';
import { openPersonalPopup } from '@features/banners/slice/personalPopupSlice';
import CitySelector from '@features/city-selector/CitySelector';
import useFavoritesSync from '@features/favorites/useFavoritesSync';
import DepositOnlineMoneyGate from '@features/deposit/screens/money-gate/zone/tj/DepositOnlineMoneyGate';
import FreespinCampaignView from '@features/freespin/components/FreespinCampaignView';
import FreespinCampaigns from '@features/freespin/FreespinCampaigns';
import GiftBoxes from '@features/giftbox/GiftBoxes';
import KenoIframe from '@features/keno/KenoIframe';
import MyBonuses from '@features/my-bonuses/MyBonuses';
import MyLimits from '@features/my-limits/MyLimits';
import NotificationSettings from '@features/notifications/NotificationSettings';
import QuizPendingClaim from '@features/quiz/QuizPendingClaim';
import Source from '@features/source/Source';
import SuperExactScore from '@features/super-exact-score/SuperExactScore';
import { useSnackbar } from 'notistack';
// import WithdrawNotification from '@features/withdrawal/components/WithdrawNotification';
import React, { lazy, Suspense, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { Route, Routes } from 'react-router-dom';

const LoginScreen = lazy(() => import( '@common/layouts/login-screen/LoginScreen'));
const MainLayout = lazy(() => import( '@common/layouts/MainLayout'));
const AccountHistory = lazy(() => import( '@features/account-history/AccountHistory'));
const Addiction = lazy(() => import( '@features/addiction/Addiction'));
const Admiral = lazy(() => import( '@features/admiral/Admiral'));
const SignIn = lazy(() => import( '@features/auth/SignIn'));
const Basket = lazy(() => import( '@features/basket/Basket'));
const BonusesRoutes = lazy(() => import( '@features/bonuses/BonusesRoutes'));
const Campaigns = lazy(() => import( '@features/campaigns/Campaigns'));
const ChampionshipsRoutes = lazy(() => import( '@features/championships/ChampionshipsRoutes'));
const Cyberzone = lazy(() => import( '@features/cyberzone/Cyberzone'));
const Deposit = lazy(() => import( '@features/deposit/Deposit'));
const DepositSuccess = lazy(() => import( '@features/deposit/success/DepositSuccess'));
const EventSearchRoutes = lazy(() => import( '@features/event-search/EventSearchRoutes'));
const Event = lazy(() => import( '@features/event/Event'));
const EventsResults = lazy(() => import( '@features/events-results/EventsResults'));
const ExactScore = lazy(() => import( '@features/exact-score/ExactScore'));
const Feedback = lazy(() => import( '@features/feedback/Feedback'));
const ForgotPassword = lazy(() => import( '@features/forgot-password/ForgotPassword'));
const Home = lazy(() => import( '@features/home/Home'));
const Keno = lazy(() => import( '@features/keno/Keno'));
const Live = lazy(() => import( '@features/live/Live'));
const MessageCEO = lazy(() => import( '@features/message-ceo/MessageCEO'));
const Messages = lazy(() => import( '@features/messages/Messages'));
const NewsRoutes = lazy(() => import( '@features/news/NewsRoutes'));
const MediaRoutes = lazy(() => import( '@features/media/MediaRoutes'));
const Regulations = lazy(() => import( '@features/regulations/Regulations'));
const Schedule = lazy(() => import( '@features/schedule/Schedule'));
const SearchTicket = lazy(() => import( '@features/search-ticket/SearchTicket'));
const Settings = lazy(() => import( '@features/settings/Settings'));
const SignUp = lazy(() => import( '@features/sign-up/SignUp'));
const SignUpSuccess = lazy(() => import( '@features/sign-up/success/SignUpSuccess'));
const SmartsoftGaming = lazy(() => import( '@features/smartsoft-gaming/SmartsoftGaming'));
const SportGames = lazy(() => import( '@features/sport-games/SportGames'));
const Stream = lazy(() => import( '@features/stream/Stream'));
const Forecast = lazy(() => import( '@features/super-express/forecast/Forecast'));
const SuperExpress = lazy(() => import( '@features/super-express/regular/SuperExpress'));
const Survey = lazy(() => import( '@features/survey/Survey'));
const TopGames = lazy(() => import( '@features/top-games/TopGames'));
const TournamentsRoutes = lazy(() => import( '@features/tournaments/TournamentsRoutes'));
const QuizScreen = lazy(() => import( '@features/quiz/QuizScreen'));
const UserBetsRoutes = lazy(() => import( '@features/user-bets/UserBetsRoutes'));
const MyFreebetsRoutes = lazy(() => import( '@features/my-freebets/MyFreebetsRoutes'));
const Profile = lazy(() => import( '@features/user/profile/Profile'));
const PromoCode = lazy(() => import( '@features/promo-code/PromoCode'));
const Verification = lazy(() => import( '@features/verification/Verification'));
const VerificationLayout = lazy(() => import( '@features/verification/VerificationLayout'));
const Wallets = lazy(() => import( '@features/wallets/Wallets'));
const Withdrawal = lazy(() => import( '@features/withdrawal/Withdrawal'));
const SurveyRuntimeHost = lazy(() => import('@features/survey/runtime/SurveyRuntimeHost'));
const Sportradar = lazy(() => import( '@features/sportradar/Sportradar'));
const AccountDeactivation = lazy(() => import( '@features/account-deactivation/AccountDeactivation'));
const SoftSwissResults = lazy(() => import( '@features/sport-games/components/SoftSwissResults'));
const DepositBonusNotify = lazy(() => import('@features/depositBonusNotify/DepositBonusNotify'));
const CashboxDepositNotification = lazy(() => import('@features/cashbox-deposit-notification/CashboxDepositNotification'));
const InsuranceRefundPopup = lazy(() => import('@features/insurance-refund-popup/InsuranceRefundPopup'));
const WorldCupForecast = lazy(() => import('@features/world-cup-forecast'));
const Engagement = lazy(() => import('@features/engagement/Engagement'));

export default function AppRoutes() {
  const dispatch = useAppDispatch();
  const loggedIn = useAppSelector(userLoggedIn);
  const userId = useAppSelector(getUserId);
  const platform = useAppSelector(selectAppPlatform);
  const { enqueueSnackbar } = useSnackbar();
  useFavoritesSync();

  useEffect(() => {
    if (loggedIn) {
      dispatch(openPersonalPopup('afterLogin'));
      dispatch(openPersonalPopup('user'));
    }
  }, [ dispatch, loggedIn ]);

  useEffect(() => {
    if (loggedIn && userId) {
      miniGamesClaimWin(userId)
        .then((status) => {
          switch (status) {
            case 200:
              enqueueSnackbar(<FormattedMessage id="FREEBET_ACTIVATED"/>, {
                variant: 'success',
                autoHideDuration: 5000,
              });
              break;
            case 409:
              enqueueSnackbar(<FormattedMessage id="FREEBET_ALREADY_ACTIVATED"/>, {
                variant: 'error',
                autoHideDuration: 5000,
              });
              break;
            default:
              break;
          }
        });
    }
  }, [ enqueueSnackbar, loggedIn, userId ]);

  return (
    <AuthDialogProvider>
      <RecommendedExpressProvider>
      <Suspense fallback={<SplashScreen/>}>
        <Routes>
          <Route element={<LoginScreen/>}>
            <Route path="/sign-in" element={<SignIn/>}/>
            {!isDomainZone('uz') && <Route path="/sign-up" element={<SignUp/>}/>}
            {!isDomainZone('uz') && <Route path="/sign-up/success" element={<SignUpSuccess/>}/>}
            <Route path="/forgot-password" element={<ForgotPassword/>}/>
          </Route>
          <Route path="/" element={<MainLayout/>}>
            <Route index element={<Home/>}/>
            <Route path="/news/*" element={<NewsRoutes/>}/>
            {!isDomainZone('ua') && !isDomainZone('boxbet') && <Route path="/media/*" element={<MediaRoutes/>}/>}
            <Route path="/bonus-previews/*" element={<BonusesRoutes/>}/>
            <Route path="/tournaments/*" element={<TournamentsRoutes/>}/>
            <Route path="/quiz/:id" element={<QuizScreen/>}/>
            <Route path="/live/*" element={<Live/>}/>
            <Route path="/top-games" element={<TopGames/>}/>
            <Route path="/prematches/*" element={<ChampionshipsRoutes/>}/>
            <Route path="/event/:id" element={<Event/>}/>
            <Route path="/my-bets/*" element={<UserBetsRoutes/>}/>
            <Route path="/my-freebets/*" element={<MyFreebetsRoutes/>}/>
            <Route path="/regulations" element={<Regulations/>}/>
            <Route path="/live-schedule" element={<Schedule/>}/>
            <Route path="/event-search/*" element={<EventSearchRoutes/>}/>
            <Route path="/message-ceo" element={<MessageCEO/>}/>
            <Route path="/feedback" element={<Feedback/>}/>
            <Route path="/messages/*" element={<Messages/>}/>
            <Route path="/results" element={<EventsResults/>}/>
            <Route path="/settings" element={<Settings/>}/>
            <Route path="/super-express" element={<SuperExpress/>}/>
            <Route path="/super-express/mma" element={<Forecast/>}/>
            <Route path="/profile/*" element={<PrivateRoute><Profile/></PrivateRoute>}/>
            {!isDomainZone('boxbet') && <Route path="/search-ticket" element={<SearchTicket/>}/>}
            <Route path="/exact-score" element={<ExactScore/>}/>
            <Route path="/super-exact-score" element={<SuperExactScore/>}/>

            <Route path="/wallets" element={<Wallets/>}/>
            <Route path="/promo-code" element={<PromoCode/>}/>
            <Route path="/account-history" element={<AccountHistory/>}/>
            {!isDomainZone('uz') && <Route path="/withdrawal" element={<Withdrawal/>}/>}
            {!isDomainZone('uz') && <Route path="/deposit" element={<Deposit/>}/>}
            {!isDomainZone('uz') && <Route path="/deposit/success" element={<DepositSuccess/>}/>}
            {!isDomainZone('uz') && <Route path="/deposit/money-gate/:id" element={<DepositOnlineMoneyGate/>}/>}
            <Route path="/streams/*" element={<Stream/>}/>
            <Route path="/addiction" element={<Addiction/>}/>
            <Route path="/sources" element={<Source/>}/>

            {!config.hideCasino && <Route path="/admiral" element={<Admiral/>}/>}
            {!config.hideCasino && <Route path="/admiral/campaigns" element={<FreespinCampaigns/>}/>}
            {!config.hideCasino && <Route path="/admiral/campaigns/:id/:type" element={<FreespinCampaignView/>}/>}

            <Route path="/my-bonuses" element={<MyBonuses/>}/>
            <Route path="/my-limits" element={<MyLimits/>}/>
            <Route path="/notifications" element={<NotificationSettings/>}/>
            <Route path="/engagement" element={<Engagement/>}/>

            <Route path="/campaigns" element={<Campaigns/>}/>
            <Route path="/survey" element={<Survey/>}/>
            <Route path="/keno-games" element={<Keno/>}/>
            <Route path="/smartsoft-gaming" element={<SmartsoftGaming/>}/>
            <Route path="/sport-games" element={<SportGames/>}/>
            {/*<Route path="/table-tennis" element={<Sportradar url={config.sportradar.iframe['/table-tennis']}/>}/>*/}
            {(isDomainZone('tj') || isDomainZone('ru') || isDomainZone('uz')) && (
              <Route path="/online-football" element={<Sportradar
                url={config.sportradar.iframe['/online-football']}/>}/>
            )}
            <Route path="/account-deactivation" element={<AccountDeactivation/>}/>
            <Route path="/world-cup-2026-forecast" element={<WorldCupForecast/>}/>
          </Route>
          {!isDomainZone('boxbet') && <Route path="/sgames/sportsbook" element={<SoftSwissResults/>}/>}
          <Route path="/keno/:name" element={<KenoIframe/>}/>

          {!config.hideCasino && <Route path="/admiral/game/:game_id" element={<AdmiralGameIframe/>}/>}
          {!isDomainZone('ua') && <Route path="/cyberzone" element={<Cyberzone/>}/>}
          <Route path="/sport-games" element={<SportGames/>}/>
          <Route path="/verification" element={<VerificationLayout/>}>
            <Route index element={<Verification/>}/>
          </Route>
          <Route path="*" element={<MainLayout/>}>
            <Route path="*" element={<Home/>}/>
          </Route>
        </Routes>
        <Basket/>
        <SurveyRuntimeHost/>
        {loggedIn && <GiftBoxes/>}
        {loggedIn && <DepositBonusNotify/>}
        {loggedIn && <CashboxDepositNotification/>}
        {loggedIn && <InsuranceRefundPopup/>}
        {loggedIn && <QuizPendingClaim/>}
        {platform === 'pmi' && <CheckerIosVersion/>}
        {(loggedIn && isDomainZone('tj')) && <CitySelector/>}
        {/*{(loggedIn && isDomainZone('tj')) && <WithdrawNotification pageName="deposit_notification"/>}*/}
      </Suspense>
      <Sidebar/>
      </RecommendedExpressProvider>
    </AuthDialogProvider>
  );
}
